import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  PriceListSchema,
  PriceListCreateInputSchema,
  PriceListUpdateInputSchema,
  type PriceList,
  type PriceListCreateInput,
  type PriceListUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import {
  type CountOptions,
  type ListOptions,
  runAverage,
  runCount,
  runList,
  runSum,
} from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `priceList` (règles de prix conditionnelles, org-wide).
 *
 * Réplique le patron `asset` : l'entrée est VALIDÉE par Zod
 * (`PriceListCreateInputSchema`) AVANT toute écriture Firestore. On n'écrit
 * jamais un document pour découvrir ensuite qu'il est invalide.
 */

export type { PriceListCreateInput };

/** Nom de la collection Firestore de la famille priceList. */
export const PRICE_LISTS_COLLECTION = 'priceLists';

/**
 * Repository typé pour la famille `priceList`.
 * Toute lecture est validée par `PriceListSchema`, toute écriture par
 * `PriceListCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface PriceListRepository {
  create(input: PriceListCreateInput): Promise<PriceList>;
  update(id: string, patch: PriceListUpdateInput): Promise<PriceList>;
  list(options?: ListOptions): Promise<PriceList[]>;
  count(options?: CountOptions): Promise<number>;
  /** Somme serveur d'un champ, memes options que `count`. Cf. list-query.ts. */
  sum(field: string, options?: CountOptions): Promise<number>;
  /** Moyenne serveur d'un champ, `null` si aucun document. Cf. list-query.ts. */
  average(field: string, options?: CountOptions): Promise<number | null>;
  get(id: string): Promise<PriceList | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<PriceList>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<PriceList>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<PriceList>;
}

const priceListConverter = createConverter<PriceList>((raw: DocumentData) => PriceListSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createPriceListRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): PriceListRepository {
  const col = collection(db, PRICE_LISTS_COLLECTION).withConverter(priceListConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<PriceList>(db, col, PRICE_LISTS_COLLECTION, getActorId);

  return {
    async create(input: PriceListCreateInput): Promise<PriceList> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore.
      const fields = PriceListCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.rules !== undefined) optional['rules'] = fields.rules;
      if (fields.appliesTo !== undefined) optional['appliesTo'] = fields.appliesTo;
      if (fields.promoCode !== undefined) optional['promoCode'] = fields.promoCode;
      if (fields.priority !== undefined) optional['priority'] = fields.priority;
      if (fields.validFrom !== undefined) optional['validFrom'] = fields.validFrom;
      if (fields.validTo !== undefined) optional['validTo'] = fields.validTo;
      if (fields.currency !== undefined) optional['currency'] = fields.currency;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;
      if (fields.couleur != null) optional['couleur'] = fields.couleur;
      if (fields.photo != null) optional['photo'] = fields.photo;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as PriceList);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return PriceListSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: PriceListUpdateInput): Promise<PriceList> {
      // Valider AVANT d'écrire : même doctrine que create.
      const fields = PriceListUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Seules les clés réellement fournies sont écrites (patch partiel, merge).
      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'écriture partielle : une ref convertie
      // exigerait le document complet.
      await updateDoc(doc(db, PRICE_LISTS_COLLECTION, id), data);

      // Relire via la ref convertie : le document réel est la source de vérité,
      // et le converter le valide avec PriceListSchema.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`priceList ${id} introuvable après mise à jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<PriceList[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async sum(field: string, options?: CountOptions): Promise<number> {
      // Sommer sans charger, meme cadrage que `count` (cf. runSum,
      // list-query.ts). Rend 0 quand aucun document ne passe le filtre.
      return runSum(col, orgId, field, options);
    },

    async average(field: string, options?: CountOptions): Promise<number | null> {
      // `null` quand aucun document ne passe le filtre : c'est ce que rend
      // Firestore, et un zero mentirait (cf. runAverage, list-query.ts).
      return runAverage(col, orgId, field, options);
    },

    async get(id: string): Promise<PriceList | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
